import { inject, injectable, optional } from "inversify";
import "reflect-metadata";
import { BehaviorSubject, Observable } from "rxjs";

import { isFeatureFlagsPlusAuthenticationServerEnable } from "@archipad/helpers/debug";
import {
    LegacyArchipadAuthService,
    Legacy_Archipad_Auth_Service_DIID,
    ArchiwebServicesClient,
    Archiweb_Services_Client_DIID,
    AuthToken,
    CredentialLoginResponse,
    Credentials,
} from "@archipad-js/archipad/archiweb-services-client";
import { getUserAspects } from "@archipad/backend/project/dependenciesManager";
import _TrackingService from "@core/drivers/tracking";
import { findConfigBundleByName } from "@core/services/config";
import _LegacyServiceManager from "@core/services/legacy-service-manager";
import { createLogger } from "@core/services/logger.service";
import Settings, { SettingsManager } from "@core/services/settings";
import { callAsAsync } from "@core/tasks/asyncCaller";
import { ProgressObserver, makeProgressHandler } from "@core/tasks/progress";

import {
    AccountInfo,
    AccountPlan,
    ContactMePayload,
    CreateAccountPayload,
    FormCredentials,
    HashedCredentials,
    Legacy_Account_Service_DIID,
    StoredCredentials,
    UpdateAccountPayload,
    UpdateCredentialsPayload,
} from "./account.service.types";
import { LoginService, Login_Service_DIID } from "./login.service";
import { UserSessionStorageService, User_Session_Storage_Service_DIID } from "./sessionStorage.service";
import { AccountService, Account_Service_DIID, LegacyUserInformation } from "./account.service";
import { TrackingService } from "@core/drivers/tracking/tracking.types";


const logger = createLogger("LegacyAccountService");

export const CREDENTIALS_STORAGE_KEY = "creds";
export const LOGIN_STORAGE_KEY = "login";
export const LOG_AS_STORAGE_KEY = "logAs";

/**
 * [PLUS-158] Delete this service and all is usages
 */
@injectable()
export class LegacyAccountService {
    private readonly currentCredentialsSub: BehaviorSubject<Credentials | null> = new BehaviorSubject(null);


    public readonly currentCredentials$ = this.currentCredentialsSub.asObservable(); // legacy, not used anymore


    constructor(
        @inject(Legacy_Archipad_Auth_Service_DIID.symbol) private readonly legacyArchipadAuthService: LegacyArchipadAuthService,
        @inject(Login_Service_DIID.symbol) private readonly loginService: LoginService,
        @inject(Account_Service_DIID.symbol) private readonly parentAccountService: AccountService,
        @inject(Archiweb_Services_Client_DIID.symbol) private readonly archiwebServicesClient: ArchiwebServicesClient,
        @inject(User_Session_Storage_Service_DIID.symbol)
        @optional()
        private readonly userSessionStorageService: UserSessionStorageService,
        protected readonly trackingService: TrackingService = _TrackingService,
    ) {
        

    }

    /**
     * Equivalent to "Extends AccountService" but keep only on single instance of AccountService
     */
    public currentAccountSub: BehaviorSubject<AccountInfo> = this.parentAccountService.currentAccountSub;
    public currentUserInformationSub: BehaviorSubject<LegacyUserInformation> = this.parentAccountService.currentUserInformationSub;
    public currentPlanSub: BehaviorSubject<AccountPlan> = this.parentAccountService.currentPlanSub;
    public currentAccount$: Observable<AccountInfo> = this.parentAccountService.currentAccount$;
    public accountUpdated$: Observable<void> = this.parentAccountService.accountUpdated$;
    public currentUserInformation$: Observable<LegacyUserInformation> = this.parentAccountService.currentUserInformation$;
    public currentPlan$: Observable<AccountPlan> = this.parentAccountService.currentPlan$;
    public isAuthenticated$: Observable<boolean> = this.parentAccountService.isAuthenticated$;
    public get isAuthenticated() {
        return this.parentAccountService.isAuthenticated;
    }
    public get currentUser(): LegacyUserInformation {
        return this.parentAccountService.currentUser;
    }
    public get currentAccount(): AccountInfo {
        return this.parentAccountService.currentAccount;
    }
    public getCurrentAccount(signal: AbortSignal, progress: ProgressObserver): Promise<AccountInfo> {
        return this.parentAccountService.getCurrentAccount(signal, progress);
    }
    public updateAccount(signal: AbortSignal, progress: ProgressObserver, account: UpdateAccountPayload): Promise<void> {
        return this.parentAccountService.updateAccount(signal, progress, account);
    }
    public async createAccount(signal: AbortSignal, progress: ProgressObserver, accountData: CreateAccountPayload): Promise<AccountInfo> {
        const p = makeProgressHandler(progress);
        p.total(1);

        if (accountData.credential && accountData.credential.password) {
            accountData.credential.password = this.loginService.hashString(accountData.credential.password);
        }

        const account = await callAsAsync(signal, p, 1, (po) =>
            this.archiwebServicesClient.makeUnauthenticatedRequest(signal, "account", "create", accountData),
        );

        return account as AccountInfo;
    }
    public getCurrentAccountPlan(signal: AbortSignal, progress: ProgressObserver): Promise<AccountPlan> {
        return this.parentAccountService.getCurrentAccountPlan(signal, progress);
    }
    public refreshCurrentPlan(signal: AbortSignal, progress: ProgressObserver): Promise<void> {
        return this.parentAccountService.refreshCurrentPlan(signal, progress);
    }
    public stopCurrentPlanRecurring(signal: AbortSignal, progress: ProgressObserver): Promise<void> {
        return this.parentAccountService.stopCurrentPlanRecurring(signal, progress);
    }
    public reactivateCurrentPlanRecurring(signal: AbortSignal, progress: ProgressObserver): Promise<void> {
        return this.parentAccountService.reactivateCurrentPlanRecurring(signal, progress);
    }
    public activateFreeTrial(signal: AbortSignal, progress: ProgressObserver): Promise<unknown> {
        return this.parentAccountService.activateFreeTrial(signal, progress);
    }
    public contactMe(signal: AbortSignal, progress: ProgressObserver, params: ContactMePayload): Promise<boolean> {
        return this.parentAccountService.contactMe(signal, progress, params);
    }
    public deleteAccount(signal: AbortSignal, progress: ProgressObserver): Promise<void> {
        return this.parentAccountService.deleteAccount(signal, progress);
    }
    public checkVAT(signal: AbortSignal, progress: ProgressObserver, vat: string): Promise<boolean> {
        return this.parentAccountService.checkVAT(signal, progress, vat);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    private retrieveAndEnableUserAspects(signal: AbortSignal, progress: ProgressObserver, accountInfo: AccountInfo): Promise<void> {
        throw new Error("Method not implemented.");
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    private _postLoginProcess(signal: AbortSignal, progress: ProgressObserver): Promise<void> {
        throw new Error("Method not implemented.");
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    private clearAccountData(): void {
        throw new Error("Method not implemented.");
    }

    /**
     * Return true if user not connected or connected with limited rights (e.g. SSOToken)
     * Else return false
     */
    public get isRestricted(): boolean {
        const explicitlyRestricted = this.currentUser && this.currentUser.isRestricted;
        return explicitlyRestricted || !this.isAuthenticated;
    }

    public get isRoot(): boolean {
        return Boolean(this.currentUserInformationSub.getValue()?.isRoot);
    }

    public get currentCredentials(): Credentials | null {
        return this.currentCredentialsSub.getValue();
    }

    public fetchCurrentAccount(): Promise<AccountInfo> {        
        return this.parentAccountService.fetchCurrentAccount();
    }

    /**
     * Forces a specific `LegacyUserInformation`, used by ipad-app
     */
    public forceUserInformation(id: number, login: string): void {
        const forgedLoginResponse = {
            userId: id,
            id: id,
            login: login,
            uniqueUserId: "ipadapp_" + id.toString(),
            authToken: {
                end: 123,
                login: login,
            },
        };
        const userInformation = new LegacyUserInformation(forgedLoginResponse, false);
        this.currentUserInformationSub.next(userInformation);
    }

    /**
     * Retrieve the last used `logAs` login from {@link sessionStorageService} if available
     * and transform it into the `logAs` login format.
     * Else falls back to localStorage default login
     */
    public async getLastLogin(): Promise<string | null> {
        const settings = SettingsManager.getDefaultSettings();
        const lastLogin = await settings.get<string>(LOGIN_STORAGE_KEY);

        if (this.userSessionStorageService && this.userSessionStorageService.getStoredLogAs()) {
            const loginFromSessionStorage = this.userSessionStorageService.getStoredLogAs();
            return loginFromSessionStorage + '#' + lastLogin;
        }

        return lastLogin;
    }

    /**
     * Uses {@link sessionStorageService} if available to retrieve the matching credentials stored via {@link SettingsManager}
     * Else falls back to localStorage default credentials
     */
    public async getLastCredentials(): Promise<StoredCredentials | null> {
        let settings: Settings;

        if (this.userSessionStorageService && this.userSessionStorageService.getStoredLogAs()) {
            const loginFromSessionStorage = this.userSessionStorageService.getStoredLogAs();
            settings = SettingsManager.getSettingsForLogin(loginFromSessionStorage);
        } else {
            settings = SettingsManager.getDefaultSettings();
        }

        const credentials = await settings.get<StoredCredentials>(CREDENTIALS_STORAGE_KEY);
        return credentials;
    }

    public async logout(): Promise<void> {
        /**
         * Remove stored credentials, but keep login/logAs to pre-fill login form
         */
        if (this.currentUser) {
            if (this.userSessionStorageService && this.currentUser.logAs) {
                const settings = SettingsManager.getSettingsForLogin(this.currentUser.logAs);
                await settings.remove(CREDENTIALS_STORAGE_KEY);
            } else {
                const settings = SettingsManager.getDefaultSettings();
                await settings.remove(CREDENTIALS_STORAGE_KEY);
            }
        }

        this.legacyArchipadAuthService.setCurrentLogin(null);
        this.legacyArchipadAuthService.setAuthToken(null);
        this.currentAccountSub.next(null);
        this.currentCredentialsSub.next(null);
        this.currentUserInformationSub.next(null);
        this.currentPlanSub.next(null);

        this.trackingService.setUser(null);
    }

    /**
     * This is the _standard_ method to log an user in.
     *
     * @param signal
     * @param progress
     * @param loginCredentials
     */
    public async loginWithCredentials(
        signal: AbortSignal,
        progress: ProgressObserver,
        loginCredentials: FormCredentials | HashedCredentials,
    ): Promise<void> {
        const p = makeProgressHandler(progress);
        p.total(3);

        let hashedPassword: string;
        if ("hashedPassword" in loginCredentials) {
            hashedPassword = loginCredentials.hashedPassword;
        } else {
            hashedPassword = this.loginService.hashString(loginCredentials.password);
        }

        const credentials = {
            login: loginCredentials.login,
            hashedPassword: hashedPassword,
        };

        await callAsAsync(signal, p, 1, () => this.storeLogin(loginCredentials.login));
        await callAsAsync(signal, p, 1, (po) => this._login(signal, po, credentials));

        /**
         * Once {@link _login()} is complete, store credentials for further uses
         */
        await callAsAsync(signal, p, 1, (po) => this.storeCredentials(credentials));
    }

    /**
     * `sso` allows an user to login with credentials provided as `GET` parameters.
     *
     * It simply by-passes the login form and tries to log the user in with the provided {@link userLogin} and {@link hashedPassword}
     * It is primarily used by the admin panel to `logAs` someone.
     *
     * @deprecated This is a pretty bad pattern, it should not be used extensively, and should at least be renamed to less confusing name
     * @param signal
     * @param progress
     * @param userLogin
     * @param hashedPassword
     * @returns
     */
    public async sso(
        signal: AbortSignal,
        progress: ProgressObserver,
        userLogin: string,
        hashedPassword: string,
    ): Promise<void> {
        const p = makeProgressHandler(progress);
        p.total(3);

        const credentials = { login: userLogin, hashedPassword: hashedPassword };

        await callAsAsync(signal, p, 1, () => this.storeLogin(credentials.login));
        await callAsAsync(signal, p, 1, () => this._login(signal, progress, credentials));

        /**
         * Once {@link _login()} is complete, store credentials for further uses
         */
        await callAsAsync(signal, p, 1, () => this.storeCredentials(credentials));
    }

    /**
     * `ssoToken` allows an user to authenticate with the provided {@link tokenInfo} and {@link userLogin} `GET` parameters.
     *
     * It by-passes the __whole login process__ and registers the extracted {@link authToken} to the underlying services.
     *
     * Since this method bypasses {@link _login()}, most {@link _login()} business logic must be reproduced here if we want the application to behave correctly.
     *
     * @deprecated Just like {@link sso()} this is a pretty bad pattern.
     * @param signal
     * @param progress
     * @param userLogin
     * @param tokenInfo
     * @param userId
     * @returns
     */
    public async ssoToken(
        signal: AbortSignal,
        progress: ProgressObserver,
        userLogin: string,
        tokenInfo: string,
        userId: number,
    ): Promise<void> {
        /**
         * Reset {@link userBundle} enabled aspects
         *
         * This needs to be done here since `ssoToken()` do not call the private `login()` method.
         */
        const userBundle = findConfigBundleByName("user");
        userBundle.setEnabledAspects([]);

        const p = makeProgressHandler(progress);
        p.total(2);

        const tokenInfoString = decodeURIComponent(tokenInfo);
        const authToken: AuthToken = JSON.parse(tokenInfoString);

        /**
         * In previous `account.ts` implementation, we wrongly assumed that `authToken.hash` could be used as `hashedPassword`.
         * We actually do not/cannot know `hashedPassword` when logging in using `sso-token` route.
         *
         * As of today, `sso-token` route is only used in `forgot-password` process.
         * User will most likely update his password, updating stored {@link currentCredentialsSub} with the new password
         * when we validate it by attempting to login with the new credentials. (@file rx-account-password-controller.ts#192)
         * This will also replace current `resetPassword` authToken with a `basic` one.
         *
         * However, if the user does not update his password in time (1 hour), the authToken renewal process will fail as it will
         * try to do so with the credentials below.
         *
         * One way to improve this behavior would be to not attempt to renew `resetPassword` authToken, as they are not meant to be renewed anyways. (even tho API allows it)
         *
         * The idea is to avoid a renew attempt if we know that it will fail.
         *
         * Sadly, {@link credentials} is often checked to know if we are currently logged in, so it needs to be set to something.
         *
         * Note: We do not set this to null/empty because it might be used as password-check dialogs (deletion)
         * Note: For the reason explained above, we do not call {@link storeCredentials} as they are not reliable for autoLogin or renewal
         */
        const credentials: Credentials = {
            login: userLogin,
            hashedPassword: crypto.randomUUID(),
        };

        const forgedLoginResponse = {
            id: null,
            uniqueUserId: null,
            userId: userId,
            login: userLogin,
            authToken: authToken,
        };

        const userInformation = new LegacyUserInformation(forgedLoginResponse, true);

        await callAsAsync(signal, p, 2, () => this.storeLogin(userLogin));

        /**
         * Old login task
         *
         * Again, since `ssoToken()` do not call the private `login()` method, we have to manually call `retrieveAndEnableUserAspects()`.
         */
        try {
            this.legacyArchipadAuthService.setCurrentLogin(userLogin);
            this.legacyArchipadAuthService.setAuthToken(authToken);

            this.currentUserInformationSub.next(userInformation);

            const currentAccount = await callAsAsync<AccountInfo>(signal, p, 1, (po) =>
                this.getCurrentAccount(signal, po),
            );

            const currentPlan = await callAsAsync<AccountPlan>(signal, p, 1, (po) =>
                this.getCurrentAccountPlan(signal, po),
            );

            /**
             * Update {@link LegacyArchipadAuthService.authToken}
             */
            this.currentAccountSub.next(currentAccount);
            this.currentCredentialsSub.next(credentials);
            this.currentPlanSub.next(currentPlan);

            await callAsAsync(signal, p, 1, (po) => this.legacy_retrieveAndEnableUserAspects(signal, po, currentAccount));

            this.trackingService.setUser({
                userId: userInformation.id,
                login: userInformation.credentialLogin,
                isRoot: userInformation.isRoot,
                logAs: userInformation.logAs,
            });
        } catch (e) {
            this.legacyArchipadAuthService.setCurrentLogin(null);
            this.legacyArchipadAuthService.setAuthToken(null);

            this.currentCredentialsSub.next(null);
            this.currentUserInformationSub.next(null);

            this.trackingService.setUser(null);

            throw e;
        }
    }


    /**
     * Logs in using {@link LoginService.login()} to validate supplied {@link credentials}
     *
     * @param signal
     * @param progress
     * @param credentials
     */
    public async validateCredentials(
        signal: AbortSignal,
        progress: ProgressObserver,
        credentials: FormCredentials,
    ): Promise<void> {
        const p = makeProgressHandler(progress);
        p.total(1);

        await callAsAsync<CredentialLoginResponse>(signal, p, 1, (po) =>
            this.loginService.login(signal, po, credentials),
        );
    }

    /**
     * Updates the remote password and stores the new password using {@link storeCredentials()}
     *
     * @param signal
     * @param progress
     * @param credentials
     */
    public async changePassword(
        signal: AbortSignal,
        progress: ProgressObserver,
        credentials: UpdateCredentialsPayload,
    ): Promise<void> {
        const p = makeProgressHandler(progress);
        p.total(1);

        credentials.password = this.loginService.hashString(credentials.password);

        await callAsAsync(signal, p, 1, () => {
            return this.archiwebServicesClient.makeAuthenticatedRequest(signal, "credential", "update", credentials);
        });

        const newCredentials = {
            login: credentials.login,
            hashedPassword: credentials.password,
        };

        this.currentCredentialsSub.next(newCredentials);

        await this.storeCredentials(newCredentials);
    }


    /**
     * Calls `account/find` using a lightweight version of `LegacyArchipadAuthService` to retrieve
     * {@link AccountInfo} during the login process.
     *
     * It then enable corresponding {@link userBundle} aspects.
     *
     * @param signal
     * @param progress
     * @param authToken
     */
    private async legacy_retrieveAndEnableUserAspects(
        signal: AbortSignal,
        progress: ProgressObserver,
        accountInfo: AccountInfo,
    ): Promise<void> {
        /**
         * Handle old `loginTasks`, essentially retrieving user dependencies with `account/find`
         * and then updating {@link ConfigServiceBundle} `userBundle`
         */
        const p = makeProgressHandler(progress);
        p.total(1);

        logger.debug("Executing loginTask");
        const userAspect = await callAsAsync(signal, p, 1, (po) => {
            const p = makeProgressHandler(po);
            p.total(1);
            return p.oldTask(1, null, (task) => getUserAspects(task, accountInfo));
        });

        const userBundle = findConfigBundleByName("user");
        userBundle.setEnabledAspects(userAspect);
    }

    /**
     * Shortcut that stores `currentLogin` in both {@link settings} and {@link currentLoginStorageService}
     *
     * @param login
     */
    private async storeLogin(login: string): Promise<void> {
        const logAs = getLogAs(login);
        let settings: Settings;

        // If logging as someone, store the login in sessionStorage if available (used to be stored on window.name)
        if (logAs !== null) {
            if (this.userSessionStorageService) {
                this.userSessionStorageService.storeLogAs(logAs);
            }
            settings = SettingsManager.getSettingsForLogin(logAs);
        } else {
            if (this.userSessionStorageService) {
                this.userSessionStorageService.clearLogAs();
            }
            settings = SettingsManager.getDefaultSettings();
        }

        await settings.set(LOGIN_STORAGE_KEY, login);
    }

    /**
     * Shortcut that stores `credentials` in both {@link settings} and {@link currentLoginStorageService}
     *
     * @param credentials
     */
    private async storeCredentials(credentials: StoredCredentials): Promise<void> {
        const logAs = getLogAs(credentials.login);
        let settings: Settings;

        if (logAs !== null) {
            settings = SettingsManager.getSettingsForLogin(logAs);
        } else {
            settings = SettingsManager.getDefaultSettings();
        }

        await settings.set(CREDENTIALS_STORAGE_KEY, credentials);
    }

    /**
     * This private {@link _login} method handles the whole login process.
     *
     * It hashes the provided credentials, make the request using {@link loginService}, update {@link userBundle} aspects, {@link currentAuthTokenSub} and {@link currentCredentialsSub} subjects.
     *
     * @param signal
     * @param progress
     * @param credentials
     * @returns
     */
    private async _login(signal: AbortSignal, progress: ProgressObserver, credentials: Credentials): Promise<void> {
        try {
            /** Reset {@link userBundle} enabled aspects */
            const userBundle = findConfigBundleByName("user");
            userBundle.setEnabledAspects([]);

            const p = makeProgressHandler(progress);
            p.total(4);

            const loginResponse = await callAsAsync(signal, p, 1, (po) =>
                this.loginService.login(signal, po, credentials),
            );

            const userInformation = new LegacyUserInformation({
                userId: loginResponse.userId,
                login: loginResponse.authToken.login,
                root: loginResponse.authToken.root,

            }, false);

            /** Login process is complete, update observables and services */
            this.legacyArchipadAuthService.setCurrentLogin(loginResponse.login);
            this.legacyArchipadAuthService.setAuthToken(loginResponse.authToken);

            this.currentUserInformationSub.next(userInformation);

            const currentAccount = await callAsAsync<AccountInfo>(signal, p, 1, (po) =>
                this.getCurrentAccount(signal, po),
            );

            const currentPlan = await callAsAsync<AccountPlan>(signal, p, 1, (po) =>
                this.getCurrentAccountPlan(signal, po),
            );

            this.currentAccountSub.next(currentAccount);
            this.currentCredentialsSub.next(credentials);
            this.currentPlanSub.next(currentPlan);

            await callAsAsync(signal, p, 1, (po) => this.legacy_retrieveAndEnableUserAspects(signal, po, currentAccount));

            this.trackingService.setUser({
                userId: userInformation.id,
                login: userInformation.credentialLogin,
                isRoot: userInformation.isRoot,
                logAs: userInformation.logAs,
            });
        } catch (error) {
            const userBundle = findConfigBundleByName("user");
            userBundle.setEnabledAspects([]);
            this.legacyArchipadAuthService.setCurrentLogin(null);
            this.legacyArchipadAuthService.setAuthToken(null);
            this.currentAccountSub.next(null);
            this.currentCredentialsSub.next(null);
            this.currentUserInformationSub.next(null);
            this.trackingService.setUser(null);
            throw error;
        }
    }
}

/**
 * Splits {@link login} or {@link currentUser.login} on `#`
 * to retrieve `loggedAs` part.
 *
 * @param login
 * @returns
 */
export function getLogAs(login?: string): string | null {
    if (!login) {
        login = getLegacyAccountService().currentUser?.credentialLogin;
    }

    if (!login || login.indexOf("#") == -1) {
        return null;
    }

    return login.substring(0, login.indexOf("#"));
}

export function getLegacyAccountService(): LegacyAccountService {
    /**
     * [PLUS-158] Remove this check 
     */
    if (isFeatureFlagsPlusAuthenticationServerEnable()) {
        throw new Error('LegacyAccountService should not be used when plusAuthenticationServer is enabled');
    }
    return _LegacyServiceManager.container.get(Legacy_Account_Service_DIID.symbol);
}
